import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { useSFetch } from "../hooks/sfetch";
import type { NotificationDto } from "../api";
import { notificationIcon, notificationLink, notificationText, notificationTone, useNotifications, type Translate } from "../services/notificationStore";
import { RelativeTime } from "../components/RelativeTime";
import { EmptyState } from "../components/EmptyState";
import { useDebounced } from "../hooks/useDebounced";
import { formatDateTime } from "../services/dateFormat";
import { useTranslation } from "../hooks/useTranslation";
import styles from "./ActivityFeed.module.css";

type Source = "all" | "system" | "notifications";

interface FeedEntry {
  id: string;
  source: "system" | "notifications";
  tone: "info" | "success" | "warning" | "error";
  title: string;
  body?: string;
  createdAt: number;
  link?: string;
  icon: string;
}

interface SystemAuditLog {
  id: number;
  action: string;
  description: string;
  createdAt: string;
  executorUserId: string;
}

const PAGE_SIZE = 30;

function notificationToEntry(n: NotificationDto, t: Translate): FeedEntry {
  const text = notificationText(n, t);
  return {
    id: `n:${n.id}`,
    source: "notifications",
    tone: notificationTone(n),
    title: text.title,
    body: text.body,
    createdAt: new Date(n.lastSeenAtUtc).getTime(),
    link: notificationLink(n),
    icon: notificationIcon(n),
  };
}

function systemAuditToEntry(s: SystemAuditLog): FeedEntry {
  return {
    id: `s:${s.id}`,
    source: "system",
    tone: "info",
    title: s.action,
    body: s.description,
    createdAt: new Date(s.createdAt).getTime(),
    icon: "fa-clipboard-list",
  };
}

export default function ActivityFeed() {
  const sfetch = useSFetch();
  const { t } = useTranslation();
  const srcLabel = (s: string) => t("ACTIVITY_FILTER_" + s.toUpperCase());
  const { notifications } = useNotifications();
  const [systemAudit, setSystemAudit] = createSignal<SystemAuditLog[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [source, setSource] = createSignal<Source>("all");
  const [search, setSearch] = createSignal("");
  const debouncedSearch = useDebounced(search, 200);
  const [visibleCount, setVisibleCount] = createSignal(PAGE_SIZE);

  onMount(async () => {
    try {
      const res = await sfetch("/api/User/AuditLog");
      if (res.ok) setSystemAudit((res.value as SystemAuditLog[]) ?? []);
    } catch { /* ignore */ }
    setLoading(false);
  });

  const merged = createMemo<FeedEntry[]>(() => {
    const out: FeedEntry[] = [];
    for (const n of notifications()) out.push(notificationToEntry(n, t));
    for (const s of systemAudit()) out.push(systemAuditToEntry(s));
    out.sort((a, b) => b.createdAt - a.createdAt);
    return out;
  });

  const filtered = createMemo(() => {
    const src = source();
    const q = debouncedSearch().trim().toLowerCase();
    return merged().filter((e) => {
      if (src !== "all" && e.source !== src) return false;
      if (q) {
        const hay = `${e.title} ${e.body ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  });

  const visible = createMemo(() => filtered().slice(0, visibleCount()));

  let sentinelRef: HTMLDivElement | undefined;
  onMount(() => {
    if (!sentinelRef || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisibleCount((c) => Math.min(filtered().length, c + PAGE_SIZE));
        }
      }
    }, { rootMargin: "200px" });
    io.observe(sentinelRef);
  });

  return (
    <div class={styles.container}>
      <div class={styles.headerRow}>
        <h1>{t("ACTIVITY_TITLE")}</h1>
        <span class={styles.entryCount}>{filtered().length} {t("ACTIVITY_ENTRIES")}</span>
      </div>

      <div class={styles.toolbar}>
        <div class={styles.search}>
          <i class="fa-solid fa-magnifying-glass" style={{ color: "var(--dark-text-muted)" }} />
          <input
            type="search"
            data-search-focus="true"
            placeholder={t("ACTIVITY_SEARCH_PLACEHOLDER")}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <For each={["all", "system", "notifications"] as Source[]}>
          {(s) => (
            <button class={styles.pill} data-active={source() === s} onClick={() => { setSource(s); setVisibleCount(PAGE_SIZE); }}>
              {srcLabel(s)}
            </button>
          )}
        </For>
      </div>

      <Show when={!loading()} fallback={<p style={{ color: "var(--dark-text-muted)" }}>{t("LOADING")}</p>}>
        <Show when={visible().length > 0} fallback={
          <EmptyState illustration="audit" title={t("ACTIVITY_EMPTY_TITLE")} hint={t("ACTIVITY_EMPTY_HINT")} />
        }>
          <div class={styles.feed}>
            <For each={visible()}>
              {(e) => (
                <div class={styles.entry}>
                  <div class={styles.entryIcon} data-tone={e.tone}>
                    <i class={`fa-solid ${e.icon}`} />
                  </div>
                  <div class={styles.entryBody}>
                    <h4>
                      <span class={styles.entrySrc}>{srcLabel(e.source)}</span>
                      {e.title}
                    </h4>
                    <Show when={e.body}>
                      <p>{e.body}</p>
                    </Show>
                  </div>
                  <span class={styles.entryTime} title={formatDateTime(e.createdAt)}>
                    <RelativeTime value={e.createdAt} />
                  </span>
                </div>
              )}
            </For>
            <Show when={visible().length < filtered().length}>
              <div ref={sentinelRef} class={styles.loadMore}>{t("ACTIVITY_LOADING_MORE")}</div>
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}
